import React, {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";

import axios from "axios";
import { useTranslation } from "react-i18next";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const PER_PAGE = 10;


/* =========================================================
   HELPERS
========================================================= */

const numberFormat = (value) => {

    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
        return "0";
    }

    return new Intl.NumberFormat("en-BD", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(number);
};


const money = (value) => {
    return `৳${numberFormat(value)}`;
};


const safeText = (
    value,
    fallback = "—"
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    return value;
};


const formatDate = (value) => {

    if (!value) {
        return "—";
    }

    try {

        const date = new Date(
            String(value).replace(
                " ",
                "T"
            )
        );

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }

        return date.toLocaleString(
            "en-BD",
            {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            }
        );

    } catch (error) {

        return value;
    }
};


/* =========================================================
   COMPONENT
========================================================= */

const BasicDatatable = () => {

    const { t } = useTranslation();


    /* =====================================================
       STATE
    ===================================================== */

    const [income, setIncome] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [pageError, setPageError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [currentPage, setCurrentPage] =
        useState(1);


    /* =====================================================
       AUTH / CURRENCY
    ===================================================== */

    const authToken =
        localStorage.getItem(
            "auth_token"
        );

    const selectedCurrency =
        localStorage.getItem(
            "selectedCurrency"
        );

    const currency =
        selectedCurrency
            ? selectedCurrency.replace(
                /"/g,
                ""
            )
            : "BDT";


    /* =====================================================
       LOAD INCOME
    ===================================================== */

    const fetchIncome = async () => {

        if (!authToken) {

            setLoading(false);

            setPageError(
                "Your login session was not found. Please login again."
            );

            return;
        }


        try {

            setLoading(true);

            setPageError("");


            const response =
                await axios.post(
                    "/apps-income-history",
                    {
                        key_s: API_KEY,
                        currency: currency,
                    }
                );


            if (
                response?.data?.status === "success" ||
                Array.isArray(response?.data?.data)
            ) {

                const rows =
                    Array.isArray(
                        response?.data?.data
                    )
                        ? response.data.data
                        : [];


                setIncome(rows);

            } else {

                setIncome([]);

                setPageError(
                    response?.data?.message ||
                    "Unable to load income history."
                );
            }


        } catch (error) {

            console.error(
                "Income History Error:",
                error
            );


            setIncome([]);

            setPageError(
                error?.response?.data?.message ||
                "Unable to load income history."
            );

        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        fetchIncome();

    }, []);


    /* =====================================================
       SEARCH
    ===================================================== */

    const filteredIncome =
        useMemo(() => {

            const keyword =
                search
                    .trim()
                    .toLowerCase();


            if (!keyword) {

                return income;
            }


            return income.filter(
                (item) => {

                    const searchable = [

                        item.id,

                        item.income_Type,

                        item.income_Amount,

                        item.date

                    ]
                        .join(" ")
                        .toLowerCase();


                    return searchable.includes(
                        keyword
                    );
                }
            );

        }, [
            income,
            search
        ]);


    /* =====================================================
       RESET PAGE AFTER SEARCH
    ===================================================== */

    useEffect(() => {

        setCurrentPage(1);

    }, [search]);


    /* =====================================================
       PAGINATION
    ===================================================== */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredIncome.length /
                PER_PAGE
            )
        );


    useEffect(() => {

        if (
            currentPage >
            totalPages
        ) {

            setCurrentPage(
                totalPages
            );
        }

    }, [
        currentPage,
        totalPages
    ]);


    const currentIncome =
        useMemo(() => {

            const start =
                (currentPage - 1) *
                PER_PAGE;


            return filteredIncome.slice(
                start,
                start + PER_PAGE
            );

        }, [
            filteredIncome,
            currentPage
        ]);


    const changePage = (page) => {

        const finalPage =
            Math.max(
                1,
                Math.min(
                    page,
                    totalPages
                )
            );


        setCurrentPage(
            finalPage
        );


        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    /* =====================================================
       PAGINATION NUMBERS
    ===================================================== */

    const pageNumbers =
        useMemo(() => {

            const pages = [];

            const start =
                Math.max(
                    1,
                    currentPage - 2
                );

            const end =
                Math.min(
                    totalPages,
                    currentPage + 2
                );


            for (
                let i = start;
                i <= end;
                i++
            ) {

                pages.push(i);
            }


            return pages;

        }, [
            currentPage,
            totalPages
        ]);


    /* =====================================================
       SUMMARY
    ===================================================== */

    const summary =
        useMemo(() => {

            let totalAmount = 0;

            const types =
                new Set();


            income.forEach(
                (item) => {

                    totalAmount +=
                        Number(
                            item?.income_Amount ||
                            0
                        );


                    if (
                        item?.income_Type
                    ) {

                        types.add(
                            item.income_Type
                        );
                    }
                }
            );


            return {

                totalRecords:
                    income.length,

                totalAmount:
                    totalAmount,

                totalTypes:
                    types.size

            };

        }, [income]);


    /* =====================================================
       SHOWING
    ===================================================== */

    const showingFrom =
        filteredIncome.length === 0
            ? 0
            : (
                (currentPage - 1) *
                PER_PAGE
            ) + 1;


    const showingTo =
        Math.min(
            currentPage *
            PER_PAGE,

            filteredIncome.length
        );


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>

            {/* =================================================
                PAGE CSS
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .income-history-page {

                    --primary: #5d50f5;
                    --primary-dark: #493be7;
                    --primary-soft: #f2f0ff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --blue: #1570ef;
                    --blue-soft: #eff8ff;

                    --orange: #f79009;
                    --orange-soft: #fff7ed;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .income-container {

                    width: 100%;
                    max-width: 1450px;

                    margin: 0 auto;
                }


                /* =========================================
                   HEADER
                ========================================= */

                .income-page-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 22px;
                }


                .income-page-title h2 {

                    margin:
                        0 0 6px;

                    color:
                        var(--text);

                    font-size: 28px;

                    line-height: 1.2;

                    font-weight: 800;
                }


                .income-page-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 14px;
                }


                .income-record-badge {

                    padding:
                        10px 16px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 12px;

                    background:
                        #ffffff;

                    color:
                        var(--text2);

                    font-size: 13px;

                    font-weight: 700;
                }


                /* =========================================
                   SUMMARY
                ========================================= */

                .income-summary-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            3,
                            minmax(0, 1fr)
                        );

                    gap: 15px;

                    margin-bottom: 20px;
                }


                .income-summary-card {

                    position: relative;

                    overflow: hidden;

                    padding: 20px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 16px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 5px 20px
                        rgba(
                            16,
                            24,
                            40,
                            .04
                        );
                }


                .income-summary-top {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    margin-bottom: 15px;
                }


                .income-summary-icon {

                    width: 45px;
                    height: 45px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 13px;

                    font-size: 18px;
                }


                .income-summary-icon.purple {

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .income-summary-icon.green {

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);
                }


                .income-summary-icon.blue {

                    background:
                        var(--blue-soft);

                    color:
                        var(--blue);
                }


                .income-summary-label {

                    color:
                        var(--muted);

                    font-size: 13px;

                    font-weight: 600;
                }


                .income-summary-value {

                    display: block;

                    color:
                        var(--text);

                    font-size: 25px;

                    line-height: 1.2;

                    font-weight: 800;
                }


                .income-summary-value.green {

                    color:
                        var(--green-dark);
                }


                /* =========================================
                   MAIN CARD
                ========================================= */

                .income-main-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 18px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 8px 30px
                        rgba(
                            16,
                            24,
                            40,
                            .05
                        );
                }


                /* =========================================
                   TOOLBAR
                ========================================= */

                .income-toolbar {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    padding: 20px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .income-toolbar-title strong {

                    display: block;

                    margin-bottom: 3px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .income-toolbar-title span {

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .income-search {

                    position: relative;

                    width: 340px;

                    max-width: 100%;
                }


                .income-search-icon {

                    position: absolute;

                    top: 50%;
                    left: 15px;

                    transform:
                        translateY(-50%);

                    color:
                        #98a2b3;

                    font-size: 16px;

                    pointer-events: none;
                }


                .income-search input {

                    width: 100%;
                    height: 46px;

                    padding:
                        0 15px
                        0 42px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 11px;

                    outline: none;

                    background:
                        #f9fafb;

                    color:
                        var(--text);

                    font-size: 14px;

                    transition:
                        .2s ease;
                }


                .income-search input:focus {

                    border-color:
                        var(--primary);

                    background:
                        #ffffff;

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            93,
                            80,
                            245,
                            .08
                        );
                }


                /* =========================================
                   TABLE
                ========================================= */

                .income-table-responsive {

                    width: 100%;

                    overflow-x: auto;
                }


                .income-table {

                    width: 100%;

                    min-width: 760px;

                    margin: 0;

                    border-collapse:
                        collapse;
                }


                .income-table thead {

                    background:
                        #f9fafb;
                }


                .income-table th {

                    padding:
                        15px 18px;

                    border-bottom:
                        1px solid
                        var(--border);

                    color:
                        var(--muted);

                    font-size: 12px;

                    font-weight: 800;

                    text-align: left;

                    text-transform:
                        uppercase;

                    letter-spacing:
                        .4px;
                }


                .income-table td {

                    padding:
                        17px 18px;

                    border-bottom:
                        1px solid
                        #f0f1f4;

                    color:
                        var(--text2);

                    font-size: 14px;

                    vertical-align:
                        middle;
                }


                .income-table tbody tr {

                    transition:
                        .15s ease;
                }


                .income-table tbody tr:hover {

                    background:
                        #fafaff;
                }


                .income-id {

                    width: 45px;
                    height: 34px;

                    display:
                        inline-flex;

                    align-items:
                        center;

                    justify-content:
                        center;

                    border-radius:
                        9px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size:
                        12px;

                    font-weight:
                        800;
                }


                .income-type {

                    display:
                        inline-flex;

                    align-items:
                        center;

                    gap:
                        8px;

                    font-weight:
                        700;

                    color:
                        var(--text2);
                }


                .income-type-dot {

                    width: 8px;
                    height: 8px;

                    flex-shrink: 0;

                    border-radius:
                        50%;

                    background:
                        var(--primary);
                }


                .income-amount {

                    display:
                        inline-flex;

                    align-items:
                        center;

                    padding:
                        7px 11px;

                    border-radius:
                        9px;

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);

                    font-size:
                        14px;

                    font-weight:
                        800;
                }


                .income-date {

                    color:
                        var(--muted);

                    font-size:
                        13px;
                }


                /* =========================================
                   EMPTY / LOADING
                ========================================= */

                .income-empty {

                    padding:
                        65px 20px;

                    text-align:
                        center;
                }


                .income-empty-icon {

                    width: 60px;
                    height: 60px;

                    display:
                        flex;

                    align-items:
                        center;

                    justify-content:
                        center;

                    margin:
                        0 auto 15px;

                    border-radius:
                        16px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size:
                        23px;
                }


                .income-empty strong {

                    display:
                        block;

                    margin-bottom:
                        6px;

                    color:
                        var(--text);

                    font-size:
                        17px;
                }


                .income-empty span {

                    color:
                        var(--muted);

                    font-size:
                        13px;
                }


                .income-loading {

                    padding:
                        60px 20px;

                    text-align:
                        center;
                }


                .income-spinner {

                    width:
                        38px;

                    height:
                        38px;

                    margin:
                        0 auto 15px;

                    border:
                        4px solid
                        #eee;

                    border-top-color:
                        var(--primary);

                    border-radius:
                        50%;

                    animation:
                        incomeSpin
                        .8s linear
                        infinite;
                }


                @keyframes incomeSpin {

                    to {
                        transform:
                            rotate(360deg);
                    }
                }


                /* =========================================
                   FOOTER
                ========================================= */

                .income-table-footer {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding: 17px 20px;

                    border-top:
                        1px solid
                        #eef0f4;
                }


                .income-showing {

                    color:
                        var(--muted);

                    font-size:
                        13px;
                }


                .income-showing strong {

                    color:
                        var(--text2);

                    font-weight:
                        700;
                }


                /* =========================================
                   PAGINATION
                ========================================= */

                .income-pagination {

                    display: flex;

                    align-items: center;

                    flex-wrap: wrap;

                    gap: 6px;
                }


                .income-page-btn {

                    min-width:
                        38px;

                    height:
                        38px;

                    padding:
                        0 11px;

                    display:
                        inline-flex;

                    align-items:
                        center;

                    justify-content:
                        center;

                    border:
                        1px solid
                        var(--border);

                    border-radius:
                        9px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text2);

                    font-size:
                        13px;

                    font-weight:
                        700;

                    cursor:
                        pointer;

                    transition:
                        .15s ease;
                }


                .income-page-btn:hover:not(:disabled) {

                    border-color:
                        var(--primary);

                    color:
                        var(--primary);

                    background:
                        var(--primary-soft);
                }


                .income-page-btn.active {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary);

                    color:
                        #ffffff;
                }


                .income-page-btn:disabled {

                    opacity:
                        .4;

                    cursor:
                        not-allowed;
                }


                /* =========================================
                   MOBILE CARD
                ========================================= */

                .income-mobile-list {

                    display:
                        none;
                }


                .income-mobile-card {

                    padding:
                        16px;

                    margin-bottom:
                        12px;

                    border:
                        1px solid
                        var(--border);

                    border-radius:
                        14px;

                    background:
                        #ffffff;
                }


                .income-mobile-header {

                    display:
                        flex;

                    align-items:
                        flex-start;

                    justify-content:
                        space-between;

                    gap:
                        12px;

                    padding-bottom:
                        13px;

                    margin-bottom:
                        13px;

                    border-bottom:
                        1px solid
                        #f0f1f4;
                }


                .income-mobile-type {

                    color:
                        var(--text);

                    font-size:
                        14px;

                    font-weight:
                        800;
                }


                .income-mobile-id {

                    color:
                        var(--muted);

                    font-size:
                        11px;
                }


                .income-mobile-row {

                    display:
                        flex;

                    align-items:
                        center;

                    justify-content:
                        space-between;

                    gap:
                        15px;

                    padding:
                        8px 0;
                }


                .income-mobile-row span {

                    color:
                        var(--muted);

                    font-size:
                        12px;
                }


                .income-mobile-row strong {

                    color:
                        var(--text2);

                    font-size:
                        13px;

                    text-align:
                        right;
                }


                /* =========================================
                   RESPONSIVE
                ========================================= */

                @media (
                    max-width: 991px
                ) {

                    .income-summary-grid {

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );
                    }

                }


                @media (
                    max-width: 767px
                ) {

                    .income-history-page {

                        padding:
                            14px;

                        border-radius:
                            14px;
                    }


                    .income-page-header {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .income-page-title h2 {

                        font-size:
                            23px;
                    }


                    .income-summary-grid {

                        grid-template-columns:
                            1fr;

                        gap:
                            10px;
                    }


                    .income-summary-card {

                        padding:
                            16px;
                    }


                    .income-toolbar {

                        align-items:
                            stretch;

                        flex-direction:
                            column;

                        padding:
                            16px;
                    }


                    .income-search {

                        width:
                            100%;
                    }


                    .income-desktop-table {

                        display:
                            none;
                    }


                    .income-mobile-list {

                        display:
                            block;

                        padding:
                            14px;

                        background:
                            #f9fafb;
                    }


                    .income-table-footer {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .income-pagination {

                        width:
                            100%;

                        justify-content:
                            center;
                    }

                }


                @media (
                    max-width: 420px
                ) {

                    .income-history-page {

                        padding:
                            10px;
                    }


                    .income-page-title h2 {

                        font-size:
                            21px;
                    }


                    .income-summary-value {

                        font-size:
                            21px;
                    }


                    .income-page-btn {

                        min-width:
                            35px;

                        height:
                            35px;
                    }

                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main
                className="income-history-page"
            >

                <div
                    className="income-container"
                >


                    {/* =========================================
                        HEADER
                    ========================================= */}

                    <div
                        className="income-page-header"
                    >

                        <div
                            className="income-page-title"
                        >

                            <h2>
                                {t(
                                    "pro_IncHis_inc_his"
                                ) || "Income History"}
                            </h2>

                            <p>
                                View and track all your
                                income transactions.
                            </p>

                        </div>


                        <div
                            className="income-record-badge"
                        >
                            {income.length} Records
                        </div>

                    </div>


                    {/* =========================================
                        SUMMARY
                    ========================================= */}

                    <div
                        className="income-summary-grid"
                    >


                        {/* TOTAL INCOME */}

                        <div
                            className="income-summary-card"
                        >

                            <div
                                className="income-summary-top"
                            >

                                <span
                                    className="income-summary-label"
                                >
                                    Total Income
                                </span>

                                <div
                                    className="income-summary-icon green"
                                >
                                    ৳
                                </div>

                            </div>


                            <strong
                                className="income-summary-value green"
                            >
                                {money(
                                    summary.totalAmount
                                )}
                            </strong>

                        </div>


                        {/* TRANSACTIONS */}

                        <div
                            className="income-summary-card"
                        >

                            <div
                                className="income-summary-top"
                            >

                                <span
                                    className="income-summary-label"
                                >
                                    Total Transactions
                                </span>

                                <div
                                    className="income-summary-icon purple"
                                >
                                    #
                                </div>

                            </div>


                            <strong
                                className="income-summary-value"
                            >
                                {numberFormat(
                                    summary.totalRecords
                                )}
                            </strong>

                        </div>


                        {/* TYPES */}

                        <div
                            className="income-summary-card"
                        >

                            <div
                                className="income-summary-top"
                            >

                                <span
                                    className="income-summary-label"
                                >
                                    Income Types
                                </span>

                                <div
                                    className="income-summary-icon blue"
                                >
                                    ✓
                                </div>

                            </div>


                            <strong
                                className="income-summary-value"
                            >
                                {numberFormat(
                                    summary.totalTypes
                                )}
                            </strong>

                        </div>

                    </div>


                    {/* =========================================
                        MAIN CARD
                    ========================================= */}

                    <div
                        className="income-main-card"
                    >


                        {/* TOOLBAR */}

                        <div
                            className="income-toolbar"
                        >

                            <div
                                className="income-toolbar-title"
                            >

                                <strong>
                                    Income Transactions
                                </strong>

                                <span>
                                    Complete history of
                                    your earnings
                                </span>

                            </div>


                            <div
                                className="income-search"
                            >

                                <span
                                    className="income-search-icon"
                                >
                                    <i
                                        className="fa fa-search"
                                        aria-hidden="true"
                                    ></i>
                                </span>


                                <input
                                    type="text"
                                    value={search}
                                    placeholder="Search income history..."
                                    onChange={(event) =>
                                        setSearch(
                                            event.target.value
                                        )
                                    }
                                />

                            </div>

                        </div>


                        {/* =====================================
                            LOADING
                        ===================================== */}

                        {loading ? (

                            <div
                                className="income-loading"
                            >

                                <div
                                    className="income-spinner"
                                ></div>

                                <strong>
                                    Loading income history...
                                </strong>

                            </div>

                        ) : pageError ? (


                            /* =================================
                               ERROR
                            ================================= */

                            <div
                                className="income-empty"
                            >

                                <div
                                    className="income-empty-icon"
                                >
                                    !
                                </div>

                                <strong>
                                    Unable to load data
                                </strong>

                                <span>
                                    {pageError}
                                </span>

                            </div>

                        ) : currentIncome.length === 0 ? (


                            /* =================================
                               EMPTY
                            ================================= */

                            <div
                                className="income-empty"
                            >

                                <div
                                    className="income-empty-icon"
                                >
                                    <i
                                        className="fa fa-inbox"
                                        aria-hidden="true"
                                    ></i>
                                </div>

                                <strong>
                                    No income history found
                                </strong>

                                <span>
                                    {search
                                        ? "No transactions match your search."
                                        : "Your income transactions will appear here."
                                    }
                                </span>

                            </div>

                        ) : (

                            <Fragment>


                                {/* =============================
                                    DESKTOP TABLE
                                ============================= */}

                                <div
                                    className="income-desktop-table"
                                >

                                    <div
                                        className="income-table-responsive"
                                    >

                                        <table
                                            className="income-table"
                                        >

                                            <thead>

                                                <tr>

                                                    <th>
                                                        ID
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_IncHis_type"
                                                        ) || "Type"}
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_IncHis_amount"
                                                        ) || "Amount"}
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_IncHis_date"
                                                        ) || "Date"}
                                                    </th>

                                                </tr>

                                            </thead>


                                            <tbody>

                                                {currentIncome.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => {

                                                        const serial =
                                                            (
                                                                (
                                                                    currentPage -
                                                                    1
                                                                ) *
                                                                PER_PAGE
                                                            ) +
                                                            index +
                                                            1;


                                                        return (

                                                            <tr
                                                                key={
                                                                    row.id ||
                                                                    serial
                                                                }
                                                            >

                                                                <td>

                                                                    <span
                                                                        className="income-id"
                                                                    >
                                                                        {serial}
                                                                    </span>

                                                                </td>


                                                                <td>

                                                                    <span
                                                                        className="income-type"
                                                                    >

                                                                        <span
                                                                            className="income-type-dot"
                                                                        ></span>

                                                                        {safeText(
                                                                            row.income_Type
                                                                        )}

                                                                    </span>

                                                                </td>


                                                                <td>

                                                                    <span
                                                                        className="income-amount"
                                                                    >
                                                                        {money(
                                                                            row.income_Amount
                                                                        )}
                                                                    </span>

                                                                </td>


                                                                <td>

                                                                    <span
                                                                        className="income-date"
                                                                    >
                                                                        {formatDate(
                                                                            row.date
                                                                        )}
                                                                    </span>

                                                                </td>

                                                            </tr>
                                                        );
                                                    }
                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                </div>


                                {/* =============================
                                    MOBILE CARDS
                                ============================= */}

                                <div
                                    className="income-mobile-list"
                                >

                                    {currentIncome.map(
                                        (
                                            row,
                                            index
                                        ) => {

                                            const serial =
                                                (
                                                    (
                                                        currentPage -
                                                        1
                                                    ) *
                                                    PER_PAGE
                                                ) +
                                                index +
                                                1;


                                            return (

                                                <div
                                                    className="income-mobile-card"
                                                    key={
                                                        row.id ||
                                                        serial
                                                    }
                                                >

                                                    <div
                                                        className="income-mobile-header"
                                                    >

                                                        <div>

                                                            <div
                                                                className="income-mobile-type"
                                                            >
                                                                {safeText(
                                                                    row.income_Type
                                                                )}
                                                            </div>

                                                            <span
                                                                className="income-mobile-id"
                                                            >
                                                                Transaction #{serial}
                                                            </span>

                                                        </div>


                                                        <span
                                                            className="income-amount"
                                                        >
                                                            {money(
                                                                row.income_Amount
                                                            )}
                                                        </span>

                                                    </div>


                                                    <div
                                                        className="income-mobile-row"
                                                    >

                                                        <span>
                                                            Type
                                                        </span>

                                                        <strong>
                                                            {safeText(
                                                                row.income_Type
                                                            )}
                                                        </strong>

                                                    </div>


                                                    <div
                                                        className="income-mobile-row"
                                                    >

                                                        <span>
                                                            Amount
                                                        </span>

                                                        <strong>
                                                            {money(
                                                                row.income_Amount
                                                            )}
                                                        </strong>

                                                    </div>


                                                    <div
                                                        className="income-mobile-row"
                                                    >

                                                        <span>
                                                            Date
                                                        </span>

                                                        <strong>
                                                            {formatDate(
                                                                row.date
                                                            )}
                                                        </strong>

                                                    </div>

                                                </div>
                                            );
                                        }
                                    )}

                                </div>

                            </Fragment>
                        )}


                        {/* =====================================
                            FOOTER + PAGINATION
                        ===================================== */}

                        {!loading &&
                            !pageError &&
                            filteredIncome.length > 0 && (

                            <div
                                className="income-table-footer"
                            >

                                <div
                                    className="income-showing"
                                >

                                    Showing{" "}

                                    <strong>
                                        {showingFrom}
                                    </strong>

                                    {" "}to{" "}

                                    <strong>
                                        {showingTo}
                                    </strong>

                                    {" "}of{" "}

                                    <strong>
                                        {filteredIncome.length}
                                    </strong>

                                    {" "}entries

                                </div>


                                <div
                                    className="income-pagination"
                                >


                                    {/* FIRST */}

                                    <button
                                        type="button"
                                        className="income-page-btn"
                                        disabled={
                                            currentPage === 1
                                        }
                                        onClick={() =>
                                            changePage(1)
                                        }
                                    >
                                        <i
                                            className="fa fa-angle-double-left"
                                            aria-hidden="true"
                                        ></i>
                                    </button>


                                    {/* PREVIOUS */}

                                    <button
                                        type="button"
                                        className="income-page-btn"
                                        disabled={
                                            currentPage === 1
                                        }
                                        onClick={() =>
                                            changePage(
                                                currentPage -
                                                1
                                            )
                                        }
                                    >
                                        <i
                                            className="fa fa-angle-left"
                                            aria-hidden="true"
                                        ></i>
                                    </button>


                                    {/* PAGE NUMBERS */}

                                    {pageNumbers.map(
                                        (page) => (

                                            <button
                                                type="button"
                                                key={page}
                                                className={
                                                    `income-page-btn ${
                                                        currentPage ===
                                                        page
                                                            ? "active"
                                                            : ""
                                                    }`
                                                }
                                                onClick={() =>
                                                    changePage(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>

                                        )
                                    )}


                                    {/* NEXT */}

                                    <button
                                        type="button"
                                        className="income-page-btn"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            changePage(
                                                currentPage +
                                                1
                                            )
                                        }
                                    >
                                        <i
                                            className="fa fa-angle-right"
                                            aria-hidden="true"
                                        ></i>
                                    </button>


                                    {/* LAST */}

                                    <button
                                        type="button"
                                        className="income-page-btn"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            changePage(
                                                totalPages
                                            )
                                        }
                                    >
                                        <i
                                            className="fa fa-angle-double-right"
                                            aria-hidden="true"
                                        ></i>
                                    </button>

                                </div>

                            </div>
                        )}

                    </div>

                </div>

            </main>

        </Fragment>
    );
};


export default BasicDatatable;